<script>
import { useVuelidate } from '@vuelidate/core';
import ButtonNext from 'next/button/Button.vue';
import Draggable from 'vuedraggable';
import { mapGetters } from 'vuex';
import getUuid from 'widget/helpers/uuid';
import { setStepMoveInProgress } from 'dashboard/store/utils/wgptAutomationBuilderUtils';
import {
  computeStepVariablesMap,
  insertStepAt,
} from '../../../helpers/automationUtils';
import AddStepButton from '../../addStepDropdown/AddStepButton.vue';
import Dropdown from '../../Dropdown.vue';
import forEachValidationMixins from './helpers/forEachValidationMixins';

export default {
  components: {
    AddStepButton,
    Dropdown,
    ButtonNext,
    Draggable,
  },
  mixins: [forEachValidationMixins],
  provide() {
    return {
      stepAncestorIds: [...this.parentStepAncestorIds, this.step.id],
      forEachMaxIterationsCap: Math.floor(this.parentMaxIterationsCap * 0.1),
    };
  },
  inject: {
    parentStepAncestorIds: {
      from: 'stepAncestorIds',
      default: () => [],
    },
    parentMaxIterationsCap: {
      from: 'forEachMaxIterationsCap',
      default: 500,
    },
  },
  props: {
    step: {
      type: Object,
      default: () => {},
    },
    first: {
      type: Boolean,
      default: true,
    },
    last: {
      type: Boolean,
      default: true,
    },
    // eslint-disable-next-line vue/no-unused-properties
    variables: {
      type: Array,
      default: () => [],
    },
    disabled: {
      type: Boolean,
      default: false,
    },
  },
  emits: ['stepDelete', 'addStepBefore', 'addStepAfter', 'stepChange'],
  setup() {
    return { v$: useVuelidate() };
  },
  computed: {
    ...mapGetters({
      actionsRegistry: 'wgptAutomationRegistry/getActions',
      actionClipboard: 'wgptAutomations/getActionClipboard',
      getLinkedSteps: 'wgptAutomationBuilder/getLinkedSteps',
    }),
    steps() {
      return this.step.steps || [];
    },
    isEmpty() {
      return this.steps.length === 0;
    },
    isBeingCut() {
      const clipboard = this.actionClipboard;
      if (!clipboard) return false;
      const { action, operation } = clipboard;
      if (operation !== 'cut') return false;
      return action.id === this.step.id;
    },
    draggableStep: {
      get() {
        return [this.step];
      },
      set(steps) {
        if (steps.length === 0) {
          this.onStepMove();
        } else if (steps.length > 1) {
          this.onStepDropped(steps);
        }
      },
    },
    draggableBody: {
      get() {
        return [];
      },
      set(steps) {
        const selectedStepId = this.$route.query.selected;
        let newSelectedId = null;

        steps.forEach(step => {
          const newId = getUuid();
          if (step.id === selectedStepId) newSelectedId = newId;
          this.addStepAt(0, { ...step, id: newId });
        });

        if (newSelectedId) {
          this.$router
            .replace({ query: { selected: newSelectedId } })
            .catch(() => {});
        }
        setStepMoveInProgress(false);
      },
    },
    stepRoute() {
      if (this.v$.step.$error) {
        return { query: { selected: this.step.id, dirty: true } };
      }

      return { query: { selected: this.step.id } };
    },
    linkedSteps() {
      return this.getLinkedSteps(this.step.id);
    },
    isActive() {
      return this.$route.query.selected === this.step.id;
    },
    isLinked() {
      const selected = this.$route.query.selected;
      return this.linkedSteps.some(s => s.id === selected);
    },
    loopVariables() {
      const fieldMapping = this.step.attributes?.field_mapping || [];
      const fieldMappingVars = fieldMapping
        .filter(m => m.variable_name)
        .map(m => ({
          name: m.variable_name,
          source: this.step.id,
          type: m.type || 'text',
        }));
      return [...this.variables, ...fieldMappingVars];
    },
    stepVariablesMap() {
      return computeStepVariablesMap(
        this.steps,
        this.actionsRegistry,
        this.loopVariables
      );
    },
  },
  methods: {
    onStepDropped(steps) {
      const droppedStepsBefore = [];
      const droppedStepsAfter = [];
      const currentStepId = this.step.id;
      let found = false;

      steps.forEach(step => {
        if (step.id === currentStepId) {
          found = true;
          return;
        }
        if (found) {
          droppedStepsAfter.push(step);
        } else {
          droppedStepsBefore.push(step);
        }
      });

      const selectedStepId = this.$route.query.selected;
      let newSelectedId = null;

      droppedStepsBefore.forEach(step => {
        const newId = getUuid();
        if (step.id === selectedStepId) newSelectedId = newId;
        this.$emit('addStepBefore', { ...step, id: newId });
      });

      droppedStepsAfter.forEach(step => {
        const newId = getUuid();
        if (step.id === selectedStepId) newSelectedId = newId;
        this.$emit('addStepAfter', { ...step, id: newId });
      });

      if (newSelectedId) {
        this.$router
          .replace({ query: { selected: newSelectedId } })
          .catch(() => {});
      }
    },
    onStepMove() {
      setStepMoveInProgress(true);
      this.deleteStep();
      setStepMoveInProgress(false);
    },
    onCardMove(evt) {
      // Prevent dropping the for_each into its own body by checking if the
      // target container is inside this component's root element.
      const forEachEl = this.$el;
      const targetEl = evt.to;
      if (forEachEl && forEachEl.contains(targetEl)) return false;
      return true;
    },
    deleteStep() {
      this.$emit('stepDelete', this.step.id);
    },
    cutStep() {
      this.$store.dispatch('wgptAutomations/cutAction', this.step);
    },
    copyStep() {
      this.$store.dispatch('wgptAutomations/copyAction', this.step);
    },
    addStepAt(index, newStep) {
      this.$emit('stepChange', this.step.id, {
        steps: insertStepAt(this.steps, index, newStep),
      });
    },
  },
};
</script>

<template>
  <div class="for-each mx-auto w-max">
    <div v-if="first" class="connector-top">
      <AddStepButton
        class="my-4"
        :class="{ invisible: disabled }"
        @add-step="e => $emit('addStepBefore', e)"
        @delete-step="e => $emit('stepDelete', e)"
      />
    </div>

    <Dropdown :disabled="disabled" class="mx-auto w-max">
      <Draggable
        v-model="draggableStep"
        item-key="id"
        group="steps"
        :move="onCardMove"
        class="draggable for-each-draggable"
        :class="{
          'step-error': v$.step.$error,
          'step-active': isActive,
          'step-cut-active': isBeingCut,
          'step-linked': isLinked,
        }"
      >
        <template #item="{ element: stepEl }">
          <router-link :to="stepRoute" replace class="block">
            <div :id="stepEl.id" class="for-each-content">
              <div
                class="py-2 px-3 border-b border-n-strong font-semibold text-sm"
              >
                <span class="text-base font-medium text-n-slate-12">
                  {{
                    stepEl.name ||
                    $t('WGPT_AUTOMATIONS.EDITOR.FOR_EACH.FALLBACK_NAME')
                  }}
                </span>
              </div>
              <div class="py-2 px-3">
                <p class="text-xs text-n-slate-11 m-0">
                  {{ $t('WGPT_AUTOMATIONS.REGISTRY.ACTIONS.FOR_EACH.NAME') }}
                </p>
              </div>
            </div>
          </router-link>
        </template>
      </Draggable>

      <template #popper>
        <div class="flex gap-1">
          <div class="bg-n-solid-2 rounded-md shadow-sm border border-n-weak">
            <ButtonNext
              color="slate"
              variant="ghost"
              size="xs"
              icon="i-lucide-copy"
              @click="copyStep"
            />
            <ButtonNext
              color="slate"
              variant="ghost"
              size="xs"
              icon="i-lucide-scissors"
              @click="cutStep"
            />
            <ButtonNext
              color="ruby"
              variant="ghost"
              size="xs"
              icon="i-lucide-trash"
              @click="deleteStep"
            />
          </div>
        </div>
      </template>
    </Dropdown>

    <div class="loop-body-line" />
    <div class="loop-body">
      <div class="loop-body-legend">
        {{ $t('WGPT_AUTOMATIONS.EDITOR.FOR_EACH.BODY_LABEL') }}
      </div>
      <AddStepButton
        :class="{
          invisible: disabled,
          hidden: disabled && isEmpty,
        }"
        @add-step="e => addStepAt(0, e)"
        @delete-step="e => $emit('stepDelete', e)"
      />
      <div v-if="!isEmpty" class="loop-body-line" />
      <div v-if="steps.length" class="loop-steps">
        <slot name="step" :step-variables-map="stepVariablesMap" />
      </div>
      <Draggable
        v-else
        v-model="draggableBody"
        class="loop-body-draggable"
        :empty-insert-threshold="24"
        item-key="id"
        :group="{ name: 'for-each-body', put: ['steps'] }"
      >
        <template #item="{ element }">
          <div class="fake">{{ element }}</div>
        </template>
      </Draggable>
    </div>

    <div class="connector-bottom">
      <AddStepButton
        class="my-4"
        :class="{ 'mb-0': last, invisible: disabled, hidden: disabled && last }"
        @add-step="e => $emit('addStepAfter', e)"
        @delete-step="e => $emit('stepDelete', e)"
      />
    </div>
  </div>
</template>

<style lang="scss" scoped>
.for-each-draggable {
  @apply relative bg-slate-100 dark:bg-slate-800/50 min-w-[200px] min-h-[75px] mx-2 rounded-xl;
}

.for-each-content {
  @apply rounded-xl shadow-sm border border-n-strong bg-n-solid-2 dark:bg-n-solid-3;
  @apply w-full relative cursor-pointer;
}

.step-cut-active {
  .for-each-content {
    @apply opacity-50;
  }
}

.step-active:not(.error) {
  .for-each-content {
    @apply outline-none;
    outline-color: var(--forEachThemeColor);
    outline-offset: 1px;
  }
}

.step-error {
  .for-each-content {
    @apply outline-none outline-n-ruby-8;
    outline-offset: 1px;
  }
}

.step-linked:not(.step-error, .step-active) {
  .for-each-content {
    @apply outline-none;
    outline-color: var(--linkedThemeColor);
    outline-offset: 1px;
    outline-style: dashed;
  }
}

.loop-body {
  @apply relative flex flex-col items-center mx-1 px-4 py-4 rounded-xl border border-dashed border-slate-300 dark:border-slate-600 min-w-[225px];
}

.loop-body-legend {
  @apply absolute ms-auto px-1.5 text-xs text-n-slate-11 bg-n-solid-1 -top-2;
}

.loop-body-draggable {
  @apply relative min-h-[1rem] w-full;
}

.loop-body-line {
  @apply w-0 h-4 mx-auto border-l-[1px] border-dashed border-slate-400 dark:border-slate-600;
}

[dir='ltr'] {
  .connector-top {
    @apply flex flex-col items-end w-1/2 pointer-events-none rounded-none border-r-[1px] border-dashed border-slate-400 dark:border-slate-600;
    --transform: translateX(50%);
  }

  .connector-bottom {
    @apply flex flex-col items-end w-1/2 pointer-events-none rounded-none border-r-[1px] border-dashed border-slate-400 dark:border-slate-600;
    --transform: translateX(50%);
  }

  .loop-body-legend {
    @apply right-4 left-auto;
  }
}

[dir='rtl'] {
  .connector-top {
    @apply flex flex-col items-end w-1/2 pointer-events-none rounded-none border-l-[1px] border-dashed border-slate-400 dark:border-slate-600;
    --transform: translateX(-50%);
  }

  .connector-bottom {
    @apply flex flex-col items-end w-1/2 pointer-events-none rounded-none border-l-[1px] border-dashed border-slate-400 dark:border-slate-600;
    --transform: translateX(-50%);
  }

  .loop-body-legend {
    @apply left-4 right-auto;
  }
}

.sortable-ghost {
  @apply border-none h-[2px] w-full absolute;
  &::before {
    @apply block !bg-n-brand rounded-full absolute w-full;
    content: '';
    height: 4px;
  }
  &:first-child::before {
    @apply -mt-4;
  }
  &:last-child::before {
    @apply mt-3;
  }
  .for-each-content {
    @apply opacity-0;
  }
}

.sortable-chosen .for-each-content {
  outline-color: transparent !important;
}

.step-draggable .sortable-ghost:first-child:last-child::before {
  @apply hidden;
}

.branch-draggable .sortable-ghost:before {
  @apply -mt-7 mx-2;
}

.condition-draggable .sortable-ghost:last-child::before {
  @apply hidden;
}
</style>
